import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock, CalendarDays, Globe, Heart, IdCard, MapPin, Paperclip, Phone, UserRound } from "lucide-react";
import { fileUrlAuth } from "../../api/fileUrl";
import { docStatus } from "./docStatus";
import { DocViewer } from "./DocViewer";
import { GenderQuickEdit } from "./GenderQuickEdit";
import { InfoRow } from "./InfoRow";

const Badge = ({ expiry }) => {
  const { t } = useTranslation();
  const status = docStatus(expiry);
  if (!status) return null;
  const tone = status.expired ? "text-danger" : "text-warning";
  return (
    <span className={`text-[11px] font-bold ${tone}`}>
      {status.expired ? t("personal.expired") : t("personal.expiresIn", { days: status.days })}
    </span>
  );
};

export const PersonalCard = ({ personal, userId, canEdit, onSaved }) => {
  const { t, i18n } = useTranslation();
  const [view, setView] = useState(null);
  const p = personal || {};
  const country = p.nationality ? new Intl.DisplayNames([i18n.language], { type: "region" }).of(p.nationality) : null;
  const files = [["national_id_file", "personal.nationalIdFile"], ["passport_file", "personal.passportFile"]].filter(([k]) => p[k]);
  return (
    <div data-testid="detail-personal">
      <h3 className="mb-1 text-sm font-bold text-foreground/80">{t("personal.title")}</h3>
      <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
        <InfoRow icon={Globe} label={t("personal.nationality")} value={country} />
        <InfoRow icon={CalendarDays} label={t("personal.birthDate")} value={p.birth_date} />
        {canEdit ? (
          <GenderQuickEdit userId={userId} personal={p} onSaved={onSaved} />
        ) : (
          <InfoRow icon={UserRound} label={t("personal.gender")} value={p.gender ? t(`personal.${p.gender}`) : null} />
        )}
        <InfoRow icon={Heart} label={t("personal.marital")} value={p.marital_status ? t(`personal.maritalOptions.${p.marital_status}`) : null} />
        <InfoRow icon={IdCard} label={t("personal.nationalId")} value={p.national_id} />
        <InfoRow icon={CalendarClock} label={t("personal.nationalIdExpiry")} value={p.national_id_expiry} badge={<Badge expiry={p.national_id_expiry} />} />
        <InfoRow icon={IdCard} label={t("personal.passport")} value={p.passport_number} />
        <InfoRow icon={CalendarClock} label={t("personal.passportExpiry")} value={p.passport_expiry} badge={<Badge expiry={p.passport_expiry} />} />
        <InfoRow icon={UserRound} label={t("personal.emergencyName")} value={p.emergency_name} />
        <InfoRow icon={Phone} label={t("personal.emergencyPhone")} value={p.emergency_phone} />
        <div className="sm:col-span-2">
          <InfoRow icon={MapPin} label={t("personal.address")} value={p.address} />
        </div>
        {files.length ? (
          <div className="sm:col-span-2">
            <InfoRow
              icon={Paperclip}
              label={t("personal.files")}
              value={
                <span className="flex gap-3">
                  {files.map(([key, label]) => (
                    <button key={key} type="button" onClick={() => setView({ url: fileUrlAuth(p[key]), label: t(label) })} className="text-primary underline" data-testid={`detail-${key}`}>
                      {t(label)}
                    </button>
                  ))}
                </span>
              }
            />
          </div>
        ) : null}
      </div>
      <DocViewer open={!!view} url={view?.url} title={view?.label} onClose={() => setView(null)} />
    </div>
  );
};
